/**
 * @copyright (C) Microsoft Corporation.
 * All Rights Reserved.
 */
import React from "react";
import { IEntraLinkingStatusDTO, convertToLocaleDateTime } from "@nuance/hap-components";
import * as Yup from "yup";
import { SelectionMode, Text, getTheme, Pivot, PivotItem } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import {
    HuxDetailsList,
    HuxFieldType,
    HuxInfoItemList,
    IHuxDetailsListItem,
    INoItemsAvailableProps,
    NoItemsType
} from "@nuance/hux-components";
import { getLogger } from "@nuance/hux-diagnostics";
import { EventTypes } from "../EventTypes";
import { EntraLinkingProgressAndMessage } from "./EntraLinkingProgressAndMessage";
import { HollowExclamationMarkSVG } from "../components/SVG/HollowExclamationMarkSVG";
import { HollowCheckMarkSVG } from "../components/SVG/HollowCheckMarkSVG";
import { Field, FieldTypes, FormPanel } from "../components/FormPanel";

const logger = getLogger();

enum RecordTabKeys {
    AllRecords = "allRecords",
    RecordsWithErrors = "recordsWithErrors"
}

/**
 * Props for the EntraLinkingDetailsPanel
 */
export interface IEntraLinkingDetailsPanelProps {
    onDismiss: () => void;
    entraLinkingDetails: IEntraLinkingStatusDTO;
    orgGuid: string;
    orgName: string;
    groupNames?: string;
}

/**
 *
 * @param props IEntraLinkingDetailsPanelProps
 * @returns JSX.Element
 */
export const EntraLinkingDetailsPanel = (props: IEntraLinkingDetailsPanelProps) => {
    const theme = getTheme();
    const { t } = useTranslation();

    /**
     * Columns for the details lists
     */
    const detailsListColumns = [
        {
            key: "nameCol",
            name: t("EntraUserLink.Name_Head"),
            fieldName: "name",
            minWidth: 100,
            maxWidth: 350,
            isResizable: true
        },
        {
            key: "usernameCol",
            name: t("EntraUserLink.Username_Head"),
            fieldName: "loginId",
            minWidth: 100,
            maxWidth: 350,
            isResizable: true
        },
        {
            key: "emailCol",
            name: t("EntraUserLink.Email_Head"),
            fieldName: "emailAddress",
            minWidth: 100,
            maxWidth: 350,
            isResizable: true
        },
        {
            key: "entraOidCol",
            name: t("EntraUserLink.EntraOID_Head"),
            fieldName: "entraOid",
            minWidth: 100,
            maxWidth: 350,
            isResizable: true
        },
        {
            key: "statusCol",
            name: t("EntraUserLink.Status_Head"),
            fieldName: "status",
            minWidth: 200,
            isMultiline: true,
            isResizable: true,
            onRender: (item: IHuxDetailsListItem) => {
                if (item.hasError) {
                    return (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row"
                            }}
                        >
                            <div
                                style={{
                                    paddingTop: "1px",
                                    paddingRight: "6px",
                                    paddingLeft: "2px"
                                }}
                            >
                                <HollowExclamationMarkSVG themedColor={theme.palette.orange} />
                            </div>
                            <Text variant="smallPlus">{item.status}</Text>
                        </div>
                    );
                } else {
                    return (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row"
                            }}
                        >
                            <div style={{ paddingRight: "6px" }}>
                                <HollowCheckMarkSVG themedColor={theme.palette.green} />
                            </div>
                            <Text variant="smallPlus" styles={{ root: { paddingTop: "1px" } }}>
                                {item.status}
                            </Text>
                        </div>
                    );
                }
            }
        }
    ];

    /**
     * Items for the details lists
     */
    const listItems: IHuxDetailsListItem[] = props.entraLinkingDetails.mappings
        ? props.entraLinkingDetails.mappings.map((record, index) => {
              return {
                  id: index.toString(),
                  index: index,
                  loginId: record.loginId,
                  name: `${record.lastName}, ${record.firstName}`,
                  emailAddress: record.emailAddress,
                  status: record.description,
                  isCompleted: record.isCompleted,
                  hasError: record.hasError,
                  entraOid: record.entraOid
              };
          })
        : [];

    /**
     * Props for no items when there are no errors
     */
    const noItemsProps: INoItemsAvailableProps = {
        heading: t("EntraUserLink.No_Errors_Text"),
        type: NoItemsType.NoErrorsFound
    };

    /**
     * Fields for the panel
     */
    const panelFields: Field<IEntraLinkingStatusDTO>[] = [
        {
            id: "messageBarAndProgressBar",
            type: FieldTypes.Custom,
            render: () => {
                return (
                    <div style={{ paddingTop: "12px" }}>
                        <EntraLinkingProgressAndMessage
                            entraLinkingInfo={props.entraLinkingDetails}
                        />
                    </div>
                );
            }
        },
        {
            id: "infoList",
            type: FieldTypes.Custom,
            render: () => {
                return (
                    <div
                        style={{
                            paddingBottom: props.groupNames ? "0px" : "12px",
                            paddingTop: "12px"
                        }}
                    >
                        <HuxInfoItemList
                            fields={[
                                {
                                    label: t("EntraUserLink.Organization_Label"),
                                    type: HuxFieldType.Text,
                                    value: props.orgName
                                }
                            ]}
                        />
                    </div>
                );
            }
        }
    ];

    if (props.groupNames) {
        panelFields.push({
            id: "infoList",
            type: FieldTypes.Custom,
            render: () => {
                return (
                    <div style={{ paddingBottom: "12px" }}>
                        <HuxInfoItemList
                            fields={[
                                {
                                    label: t("EntraUserLink.Groups_Label"),
                                    type: HuxFieldType.Text,
                                    value: props.groupNames
                                }
                            ]}
                        />
                    </div>
                );
            }
        });
    }

    panelFields.push({
        id: "userMappingList",
        type: FieldTypes.Custom,
        render: () => {
            return (
                <Pivot
                    onLinkClick={tabItem => {
                        if (tabItem?.props.itemKey === RecordTabKeys.RecordsWithErrors) {
                            logger.trackEvent(EventTypes.EntraLinkingRecordsWithErrorsViewed, {
                                importGuid: props.entraLinkingDetails.entraLinkingTaskGuid,
                                initiatedBy: props.entraLinkingDetails.initiatedBy
                            });
                        }
                    }}
                >
                    <PivotItem
                        itemKey={RecordTabKeys.AllRecords}
                        headerText={t("EntraUserLink.Successful_Matches", {
                            0: listItems.filter(item => item.isCompleted && !item.hasError).length
                        })}
                    >
                        <HuxDetailsList
                            id={RecordTabKeys.AllRecords}
                            columns={detailsListColumns}
                            items={listItems.filter(item => item.isCompleted && !item.hasError)}
                            selectionMode={SelectionMode.none}
                        />
                    </PivotItem>
                    <PivotItem
                        itemKey={RecordTabKeys.RecordsWithErrors}
                        headerText={t("EntraUserLink.Unsuccessful_Matches", {
                            0: listItems.filter(item => item.isCompleted && item.hasError).length
                        })}
                    >
                        <HuxDetailsList
                            id={RecordTabKeys.RecordsWithErrors}
                            columns={detailsListColumns}
                            items={listItems.filter(item => item.isCompleted && item.hasError)}
                            selectionMode={SelectionMode.none}
                            emptyStateProps={noItemsProps}
                        />
                    </PivotItem>
                </Pivot>
            );
        }
    });

    return (
        <FormPanel
            saveAction={() => {
                return Promise.resolve();
            }}
            onDismiss={props.onDismiss}
            initialValues={props.entraLinkingDetails}
            validationSchema={Yup.object()}
            header={t("EntraUserLink.Details_Title", {
                DateTime: convertToLocaleDateTime(new Date(props.entraLinkingDetails.submittedAt))
            })}
            fields={panelFields}
            readOnlyFooter
            customWidth="93%"
        />
    );
};
